// Standalone, store-independent holder for the asset-class selection and the scoped-route catalog.
//
// The HTTP fetcher must read the selected asset class to scope outgoing API calls, but it cannot
// import the Vuex store (that would close the cycle fetcher -> authentication -> store -> permissions
// -> fetcher). This module is the bridge: the Vuex module and the selector component write through
// it, the fetcher reads from it. localStorage keeps the routes catalog as a cold-start cache and the
// last explicit asset-class choice.

import { provisionalAssetClass } from './assetClassEntry'

const SLUG_KEY = 'okv-asset-class'
const ROUTES_KEY = 'okv-scoped-routes'

let _slug
let _routes

// The asset class of this page load. Decided from the URL (see assetClassEntry) and only then from
// the last explicit choice, so that modules evaluated at import time — before user/info — already
// see the right slug, and two tabs on two asset classes never read each other's.
export function getSelectedAssetClass() {
  if (_slug === undefined) {
    _slug = provisionalAssetClass(window.location.pathname, getRememberedAssetClass())
  }
  return _slug
}

// In-memory only: the slug of this page load. Never touches the last-choice memory.
export function setSelectedAssetClass(slug) {
  _slug = slug || null
}

// The last asset class the user explicitly chose, shared by all tabs and used only to start
// un-prefixed URLs.
export function getRememberedAssetClass() {
  return localStorage.getItem(SLUG_KEY) || null
}

export function rememberAssetClass(slug) {
  if (slug) {
    localStorage.setItem(SLUG_KEY, slug)
  } else {
    localStorage.removeItem(SLUG_KEY)
  }
}

export function getScopedRoutes() {
  if (_routes === undefined) {
    try {
      const raw = localStorage.getItem(ROUTES_KEY)
      _routes = raw ? JSON.parse(raw) : []
    } catch (e) {
      _routes = []
    }
  }
  return _routes
}

export function setScopedRoutes(routes) {
  _routes = Array.isArray(routes) ? routes : []
  localStorage.setItem(ROUTES_KEY, JSON.stringify(_routes))
}

// Namespaces a localStorage key by the selected asset class so per-asset-class user state
// (saved searches, chart presets) does not leak across asset classes when switching: the
// switch triggers a full reload, and without this every asset class would read the same key.
// Reads the slug from the standalone holder (not the store) so it works before store init.
// Falls back to the bare key when no asset class is selected (single-asset-class accounts).
export function assetClassStorageKey(baseKey) {
  const slug = getSelectedAssetClass()
  return slug ? `${baseKey}__${slug}` : baseKey
}
